import { useEffect, useMemo, useState } from 'react'
import { useDebouncedValue } from './useDebouncedValue'
import type { ListParams, PaginationMeta } from '@/types/api'

interface Service<T> {
  list: (params?: ListParams) => Promise<{ data: T[]; meta: PaginationMeta }>
}

interface Options {
  /** Tamanho de página inicial. Default 25 (regra global). */
  size?: number
  /** Trigger extra que invalida o load (ex.: contagem de updates locais). */
  reloadKey?: unknown
  /**
   * Filtros adicionais enviados ao backend (ex.: status, plan_id). São
   * concatenados aos params junto com search/sort/page/size. Sempre que mudam,
   * o hook volta para a página 1 automaticamente.
   */
  filters?: Record<string, unknown>
}

/**
 * Opções globais de page-size oferecidas em todas as listas (regra global #1).
 */
export const PAGE_SIZE_OPTIONS = [25, 50, 75, 100] as const
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number]

/** Hook genérico para páginas de listagem (busca debounced + paginação + sort + filtros). */
export function useCrudList<T>(service: Service<T>, options: Options = {}) {
  const initialSize = (options.size ?? 25) as PageSize
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(initialSize)
  const [rows, setRows] = useState<T[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [sort, setSort] = useState<{ field: string | null; direction: 'asc' | 'desc' }>({
    field: null,
    direction: 'asc',
  })
  const debouncedSearch = useDebouncedValue(search, 300)

  // Reseta página quando search/size/filters mudam.
  useEffect(() => { setPage(1) }, [debouncedSearch, size])
  // Sinatura estável para filtros (string ordenada das chaves+values).
  const filtersKey = useMemo(
    () => JSON.stringify(options.filters ?? {}),
    [options.filters],
  )
  useEffect(() => { setPage(1) }, [filtersKey])

  useEffect(() => {
    let alive = true
    setLoading(true)
    const params: ListParams = {
      page,
      size,
      search: debouncedSearch || undefined,
      ...(options.filters ?? {}),
    }
    if (sort.field) {
      params.sort = sort.field
      params.direction = sort.direction
    }
    service.list(params)
      .then((res) => {
        if (!alive) return
        setRows(res.data)
        setMeta(res.meta)
      })
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, debouncedSearch, sort.field, sort.direction, options.reloadKey, filtersKey])

  return {
    search, setSearch,
    page, setPage,
    size, setSize,
    rows, meta, loading,
    sort, setSort,
  }
}
